import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';

import MovedTo from './components/MovedTo';
import { Container, SkeletonRow, Stack } from '@/components/ui';
import { RequireAuth } from './auth';
import Layout from './components/Layout';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';

const Monitoring = lazy(() => import('./pages/Monitoring'));
const Priority = lazy(() => import('./pages/Priority'));
const Browser = lazy(() => import('./pages/Browser'));
const DiscoveryPage = lazy(() =>
  import('./pages/Discovery').then((m) => ({ default: m.DiscoveryPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.SettingsPage }))
);
const HacsBrowse = lazy(() => import('./pages/HacsBrowse'));
const HacsPackages = lazy(() => import('./pages/HacsPackages'));
const HomePage = lazy(() => import('./pages/Home'));
const ReposPage = lazy(() => import('./pages/Repos'));
const StackPulsePage = lazy(() =>
  import('./pages/StackPulse').then((m) => ({ default: m.StackPulsePage }))
);
const DevSettingsPage = lazy(() => import('./pages/Development/SettingsPage'));
const GradientBenchPage = lazy(
  () => import('./pages/Development/GradientBench')
);
const FleetIndexPage = lazy(() => import('./pages/Monitor/Fleet'));
const FleetViewPage = lazy(() => import('./pages/Monitor/Fleet/FleetView'));
const FleetConnectivityPage = lazy(
  () => import('./pages/Monitor/Fleet/Connectivity')
);
const DeviceDetailPage = lazy(
  () => import('./pages/Monitor/Fleet/DeviceDetail')
);
const PartnerApiMonitorPage = lazy(() => import('./pages/Monitor/PartnerApi'));
const ComputeSchedulerMonitorPage = lazy(
  () => import('./pages/Monitor/ComputeScheduler')
);
const GoalsPage = lazy(() =>
  import('./pages/Goals').then((m) => ({ default: m.GoalsPage }))
);
const CommandPage = lazy(() => import('./pages/GrowthOS/Command'));
const DealTriagePage = lazy(() => import('./pages/GrowthOS/DealTriage'));
const DiscoverPage = lazy(() => import('./pages/GrowthOS/Discover'));
const EmailsPage = lazy(() => import('./pages/GrowthOS/Emails'));
const LinksPage = lazy(() => import('./pages/GrowthOS/Links'));
const ScannerPage = lazy(() => import('./pages/GrowthOS/Scanner'));
const ScoreboardPage = lazy(() => import('./pages/GrowthOS/Scoreboard'));
const FinancePage = lazy(() => import('./pages/GrowthOS/Finance'));
const ForecastPage = lazy(() => import('./pages/GrowthOS/Forecast'));
const PipelinePage = lazy(() => import('./pages/GrowthOS/Pipeline'));
const OrgDetailPage = lazy(() => import('./pages/GrowthOS/Pipeline/Detail'));
const ResearchPage = lazy(() => import('./pages/GrowthOS/Research'));
const RhythmPage = lazy(() => import('./pages/GrowthOS/Rhythm'));
const TimeBoardPage = lazy(() => import('./pages/GrowthOS/TimeBoard'));
const DeviceIntelPage = lazy(() => import('./pages/DeviceIntel'));
const DeviceIntelDetail = lazy(
  () => import('./pages/DeviceIntel/DeviceIntelDetail')
);
const DeviceIntelForm = lazy(
  () => import('./pages/DeviceIntel/DeviceIntelForm')
);
const InventoryDashboardPage = lazy(
  () => import('./pages/Inventory/Dashboard')
);
const WattsonLogsPage = lazy(() => import('./pages/WattsonLogs'));
const TariffsPage = lazy(() => import('./pages/Tariffs'));
const PartsPage = lazy(() => import('./pages/Inventory/Parts'));
const PartDetailPage = lazy(() => import('./pages/Inventory/Parts/Detail'));
const BomsPage = lazy(() => import('./pages/Inventory/Boms'));
const BomDetailPage = lazy(() => import('./pages/Inventory/Boms/Detail'));
const SkusPage = lazy(() => import('./pages/Inventory/Skus'));
const SkuDetailPage = lazy(() => import('./pages/Inventory/Skus/Detail'));
const BuildOrdersPage = lazy(() => import('./pages/Inventory/BuildOrders'));
const EnergyRoutersPage = lazy(() => import('./pages/Inventory/EnergyRouters'));
const MrpPage = lazy(() => import('./pages/Inventory/Mrp'));
const PurchaseOrdersPage = lazy(
  () => import('./pages/Inventory/PurchaseOrders')
);
const PODetailPage = lazy(
  () => import('./pages/Inventory/PurchaseOrders/Detail')
);
const FleetOrdersPage = lazy(() => import('./pages/Inventory/FleetOrders'));
const FulfillmentOrdersPage = lazy(
  () => import('./pages/Inventory/FulfillmentOrders')
);
const SuppliersPage = lazy(() => import('./pages/Inventory/Suppliers'));

// Lazy-loaded so report.css stays out of the main bundle. Its @page rule and
// unscoped @media print block restyle the whole document (page size, margins,
// hidden sidebar, footer) — @page can't be class-scoped, so loading it only on
// this route is what keeps printing any other page unaffected.
const FleetConnectivityReportPage = lazy(
  () => import('./pages/Monitor/Fleet/Connectivity/report')
);

function ReportRouteFallback() {
  return (
    <Container size="xl">
      <Stack direction="vertical" spacing="sm">
        {Array.from({ length: 5 }, (_, index) => (
          <SkeletonRow key={index} columns={4} />
        ))}
      </Stack>
    </Container>
  );
}

/** Preserves :fleetId when redirecting a bookmarked legacy fleet-view URL. */
function LegacyFleetViewRedirect() {
  const { fleetId } = useParams();
  return <Navigate to={`/monitor/fleet/${fleetId}`} replace />;
}

/** Preserves :fleetId/:uuid when redirecting a bookmarked legacy device-detail URL. */
function LegacyDeviceDetailRedirect() {
  const { fleetId, uuid } = useParams();
  return <Navigate to={`/monitor/fleet/${fleetId}/devices/${uuid}`} replace />;
}

function App() {
  return (
    <Routes>
      {/* Auth callback route - handles OAuth2 redirect */}
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected routes - require authentication */}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<HomePage />} />
        {/* Growth OS section */}
        <Route path="growth-os">
          <Route index element={<Navigate to="/growth-os/command" replace />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="pipeline/:id" element={<OrgDetailPage />} />
          <Route path="research" element={<ResearchPage />} />
          <Route path="discover" element={<DiscoverPage />} />
          <Route path="command" element={<CommandPage />} />
          <Route path="deal-triage" element={<DealTriagePage />} />
          <Route path="emails" element={<EmailsPage />} />
          <Route path="links" element={<LinksPage />} />
          <Route path="scanner" element={<ScannerPage />} />
          <Route path="scoreboard" element={<ScoreboardPage />} />
          <Route path="rhythm" element={<RhythmPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="forecast" element={<ForecastPage />} />
          <Route path="time-board" element={<TimeBoardPage />} />
        </Route>
        {/* Inventory section */}
        <Route path="inventory">
          <Route
            index
            element={<Navigate to="/inventory/dashboard" replace />}
          />
          <Route path="dashboard" element={<InventoryDashboardPage />} />
          <Route path="parts" element={<PartsPage />} />
          <Route path="parts/:partId" element={<PartDetailPage />} />
          <Route path="boms" element={<BomsPage />} />
          <Route path="boms/:bomId" element={<BomDetailPage />} />
          <Route
            path="builds"
            element={<Navigate to="/inventory/build-orders" replace />}
          />
          <Route path="skus" element={<SkusPage />} />
          <Route path="skus/:skuId" element={<SkuDetailPage />} />
          <Route path="build-orders" element={<BuildOrdersPage />} />
          <Route path="energy-routers" element={<EnergyRoutersPage />} />
          <Route path="mrp" element={<MrpPage />} />
          <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="purchase-orders/:poId" element={<PODetailPage />} />
          <Route path="orders" element={<MovedTo to="/orders/customer" />} />
          <Route
            path="fulfillment"
            element={<MovedTo to="/orders/fulfillment" />}
          />
          <Route path="suppliers" element={<SuppliersPage />} />
        </Route>
        {/* Orders section */}
        <Route path="orders">
          <Route index element={<Navigate to="/orders/customer" replace />} />
          <Route path="customer" element={<FleetOrdersPage />} />
          <Route path="fulfillment" element={<FulfillmentOrdersPage />} />
        </Route>
        {/* Accounting section removed — costing lives in inventory now.
            Redirect retired bookmarks to the inventory dashboard (where the
            finance CSV exports now live). */}
        <Route
          path="accounting/*"
          element={<Navigate to="/inventory/dashboard" replace />}
        />
        {/* Integrations section */}
        <Route path="integrations">
          <Route
            index
            element={<Navigate to="/integrations/monitoring" replace />}
          />
          <Route path="monitoring" element={<Monitoring />} />
          <Route path="priority" element={<Priority />} />
          <Route path="browser" element={<Browser />} />
          <Route path="discovery" element={<DiscoveryPage />} />
          <Route path="device-intel" element={<DeviceIntelPage />} />
          <Route path="device-intel/new" element={<DeviceIntelForm />} />
          <Route path="device-intel/:oemName" element={<DeviceIntelDetail />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>
        {/* Monitor section */}
        <Route path="monitor">
          <Route index element={<Navigate to="/monitor/fleet" replace />} />
          <Route path="fleet" element={<FleetIndexPage />} />
          <Route path="fleet/:fleetId" element={<FleetViewPage />} />
          <Route
            path="fleet/:fleetId/connectivity"
            element={<FleetConnectivityPage />}
          />
          <Route
            path="fleet/:fleetId/connectivity/report"
            element={
              <Suspense fallback={<ReportRouteFallback />}>
                <FleetConnectivityReportPage />
              </Suspense>
            }
          />
          <Route
            path="fleet/:fleetId/devices/:uuid"
            element={<DeviceDetailPage />}
          />
          <Route path="tariffs" element={<TariffsPage />} />
          <Route path="logs" element={<WattsonLogsPage />} />
          <Route path="partner-api" element={<PartnerApiMonitorPage />} />
          <Route
            path="compute-scheduler"
            element={<ComputeSchedulerMonitorPage />}
          />
        </Route>
        {/* Development section */}
        <Route path="development">
          <Route index element={<Navigate to="/development/repos" replace />} />
          <Route path="repos" element={<ReposPage />} />
          <Route path="gradient-bench" element={<GradientBenchPage />} />
          <Route path="stack-pulse" element={<StackPulsePage />} />
          <Route path="goals" element={<GoalsPage />} />
          <Route
            path="fleet"
            element={<Navigate to="/monitor/fleet" replace />}
          />
          <Route path="fleet/:fleetId" element={<LegacyFleetViewRedirect />} />
          <Route
            path="fleet/:fleetId/devices/:uuid"
            element={<LegacyDeviceDetailRedirect />}
          />
          <Route
            path="tariffs"
            element={<Navigate to="/monitor/tariffs" replace />}
          />
          <Route path="settings" element={<DevSettingsPage />} />
        </Route>
        {/* Wattson section */}
        <Route path="wattson">
          <Route index element={<Navigate to="/monitor/logs" replace />} />
          <Route
            path="logs"
            element={<Navigate to="/monitor/logs" replace />}
          />
        </Route>
        {/* Home Assistant section */}
        <Route path="home-assistant">
          <Route
            index
            element={<Navigate to="/home-assistant/browse" replace />}
          />
          <Route path="browse" element={<HacsBrowse />} />
          <Route path="packages" element={<HacsPackages />} />
        </Route>
        {/* Catch-all — any unmatched path under "/" (Layout's mount point)
            renders the 404 page with the sidebar still present. This single
            catch-all covers bad top-level paths too, since Layout is mounted
            at "/" and this is its last child route. */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

export default App;
